import { MantineProvider } from '@mantine/core';
import _sum from 'lodash/sum';
import type { DataTableColumn } from 'mantine-datatable';
import { DataTable } from 'mantine-datatable';

import type { EstimationModelResponse } from '@/api/schema/data-contracts';
import { FormattedNumber } from '@/components/atoms/FormattedNumber';
import { useMessages } from '@/hooks/messages';
import type {
  CostEstimationItemsTableRecord,
  CostModel,
  ResourceEstimationItemsTableRecord,
} from '@/pages/estimation/detail/model';
import { sortCostTypes } from '@/utils/sorting';

import { estimationItemsTableMessageDefinitions } from './messages';

interface Row {
  id: '';
}

export interface SummaryTableProps {
  resourceEstimationItems: ResourceEstimationItemsTableRecord[];
  costEstimationItems: CostEstimationItemsTableRecord[];
  costs?: CostModel;
  estimationModel: EstimationModelResponse;
  hiddenCostTypeColumns?: string[];
}

const SummaryTable: React.FC<SummaryTableProps> = ({
  resourceEstimationItems = [],
  costEstimationItems = [],
  estimationModel,
  costs,
  hiddenCostTypeColumns = [],
}: SummaryTableProps) => {
  const messages = useMessages(estimationItemsTableMessageDefinitions);

  // Create a map to deduplicate cost types by ID
  const uniqueCostTypes = new Map();

  // Use sortCostTypes to get properly ordered cost types, then deduplicate by ID
  // (groups override standalone if there are duplicates)
  const allCostTypes = sortCostTypes(estimationModel.costTypes, estimationModel.costTypeGroups);

  allCostTypes.forEach((costType) => {
    uniqueCostTypes.set(costType.id, costType);
  });

  // Convert back to array maintaining the order from sortCostTypes
  const orderedCostTypes = allCostTypes.filter((ct) => uniqueCostTypes.has(ct.id));

  const costsColumns: DataTableColumn<Row>[] = orderedCostTypes
    .filter((costType) => !hiddenCostTypeColumns.includes(costType.id))
    .map((costType) => {
      return {
        accessor: 'costType' + costType.id,
        title: costType.name,
        noWrap: true,
        width: 200,
        textAlign: 'right',
        hidden: !costs,
        titleClassName: 'whitespace-break-spaces',
        render: () => {
          if (!costs) {
            return undefined;
          }

          const calculatedResourceItemsCost = _sum(
            resourceEstimationItems
              .filter((estimationItem) => !estimationItem.isOmitted)
              .map((estimationItem) => {
                const resourcesForCostType = estimationItem.resources?.filter((resource) =>
                  costs.resourceTypeCosts.find(
                    (resourceTypeCost) =>
                      resourceTypeCost.costTypeId === costType.id &&
                      resourceTypeCost.resourceTypeId === resource.resourceTypeId,
                  ),
                );
                const estimationItemCost = _sum(
                  resourcesForCostType?.map(
                    (resource) =>
                      (resource?.value ?? 0) *
                      (costs.resourceTypeCosts.find(
                        (resourceTypeCost) =>
                          resourceTypeCost.costTypeId === costType.id &&
                          resourceTypeCost.resourceTypeId === resource.resourceTypeId,
                      )?.price ?? 0),
                  ) ?? [],
                );
                return estimationItemCost;
              }),
          );

          const calculatedCostItemsCost = _sum(
            costEstimationItems
              .filter((estimationItem) => !estimationItem.isOmitted)
              .map((estimationItem) =>
                _sum(
                  estimationItem.estimationItemCosts
                    .filter((itemCost) => itemCost.costTypeId === costType.id)
                    .map((itemCost) => itemCost.price ?? 0),
                ),
              ),
          );
          return <FormattedNumber value={calculatedResourceItemsCost + calculatedCostItemsCost} />;
        },
      };
    });

  const columns: DataTableColumn<Row>[] = [
    {
      accessor: 'hierarchicalOrderReference_placeholder',
      title: '',
      sortable: false,
      width: 50,
      render: () => null,
    },
    {
      accessor: 'name_placeholder',
      title: '',
      sortable: false,
      width: 300,
      render: () => null,
    },
    {
      accessor: 'buttons_placeholder',
      title: '',
      width: 150,
      render: () => null,
    },
    {
      accessor: '',
      title: '',
      width: 140,
      render: () => null,
    },
    ...costsColumns,
    {
      accessor: 'costSum',
      title: messages.totalSum(),
      noWrap: true,
      width: 200,
      textAlign: 'right',
      titleClassName: 'whitespace-break-spaces',
      render: () => {
        if (!costs) {
          return undefined;
        }

        const calculatedResourceItemsCost = _sum(
          resourceEstimationItems
            .filter((estimationItem) => !estimationItem.isOmitted)
            .map((estimationItem) =>
              _sum(
                (estimationItem.resources ?? []).map(
                  (resource) =>
                    (resource?.value ?? 0) *
                    (costs.resourceTypeCosts.find(
                      (resourceTypeCost) =>
                        resourceTypeCost.resourceTypeId === resource.resourceTypeId,
                    )?.price ?? 0),
                ),
              ),
            ),
        );

        const calculatedCostItemsCost = _sum(
          costEstimationItems
            .filter((estimationItem) => !estimationItem.isOmitted)
            .map((estimationItem) =>
              _sum(estimationItem.estimationItemCosts.map((itemCost) => itemCost.price ?? 0)),
            ),
        );
        return <FormattedNumber value={calculatedResourceItemsCost + calculatedCostItemsCost} />;
      },
    },
  ];

  return (
    <div>
      <MantineProvider>
        <div className='relative'>
          <div className='pointer-events-none absolute top-0 right-0 z-[5] block h-full w-8 bg-gradient-to-l from-white/80 to-transparent dark:from-black/80' />
          <DataTable<Row>
            highlightOnHover
            withTableBorder
            className='table-hover mt-4 rounded-xl p-2 whitespace-nowrap'
            records={[
              {
                id: '' /* id is needed just to prevent react warning about missing key property */,
              },
            ]}
            columns={columns}
          />
        </div>
      </MantineProvider>
    </div>
  );
};

export default SummaryTable;
